/**
 * MUI Theme Configuration
 *
 * Creates MUI themes that sync with our CSS variable system.
 * MUI components need their own ThemeProvider to respect dark mode -
 * they don't automatically read CSS variables.
 *
 * ⚠ HAND-SYNCED DUPLICATE of src/styles/variables.css (slice 6 note).
 * MUI is quarantined to components/Atlas/** — this file matters only
 * there. Until it's generated from the tokens, any change to these
 * values in variables.css MUST be mirrored here, and vice versa:
 *   --gray-50…950, --color-primary(-hover/-light/-dark),
 *   --color-accent, --color-success/warning/error(-light/-dark)
 * Checklist when editing: (1) change variables.css first, (2) mirror
 * here, (3) verify an Atlas page in both themes.
 */

import { createTheme } from '@mui/material/styles';

// Color values matching variables.css
const colors = {
  // Gray scale (matching --gray-* variables)
  gray: {
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#d4d4d8',
    400: '#a1a1aa',
    500: '#71717a',
    600: '#52525b',
    700: '#3f3f46',
    800: '#27272a',
    900: '#18181b',
    950: '#09090b',
  },
  // Primary (matching --color-primary)
  primary: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#1d4ed8',
  },
  // Success (matching --color-success)
  success: {
    main: '#10b981',
    light: '#34d399',
    dark: '#059669',
  },
  // Warning (matching --color-warning)
  warning: {
    main: '#f59e0b',
    light: '#fcd34d',
    dark: '#d97706',
  },
  // Error (matching --color-error)
  error: {
    main: '#ef4444',
    light: '#fca5a5',
    dark: '#b91c1c',
  },
  // Info (matching --color-info)
  info: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#1d4ed8',
  },
};

// Shared theme settings
const baseTheme = {
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 14,
  },
  shape: {
    borderRadius: 8, // matches --radius-lg
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default gradient overlay
        },
      },
    },
  },
};

/**
 * Light theme configuration
 */
export const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: {
      main: colors.gray[600],
      light: colors.gray[400],
      dark: colors.gray[800],
    },
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    background: {
      default: '#ffffff', // --color-background light
      paper: '#ffffff',   // --color-surface light
    },
    text: {
      primary: colors.gray[900],   // --color-text light
      secondary: colors.gray[600], // --color-text-muted light
      disabled: colors.gray[500],  // --color-text-subtle light
    },
    divider: colors.gray[200], // --color-border light
    action: {
      hover: colors.gray[50],  // --color-surface-hover light
      selected: colors.gray[100],
      disabled: colors.gray[400],
      disabledBackground: colors.gray[100],
    },
  },
});

/**
 * Dark theme configuration
 */
export const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: colors.primary,
    secondary: {
      main: colors.gray[400],
      light: colors.gray[300],
      dark: colors.gray[500],
    },
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    background: {
      default: colors.gray[950], // --color-background dark
      paper: colors.gray[900],   // --color-surface dark
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.95)',  // --color-text dark
      secondary: 'rgba(255, 255, 255, 0.7)', // --color-text-muted dark
      disabled: 'rgba(255, 255, 255, 0.5)',  // --color-text-subtle dark
    },
    divider: 'rgba(255, 255, 255, 0.1)', // --color-border dark
    action: {
      hover: colors.gray[800],    // --color-surface-hover dark
      selected: colors.gray[700], // --color-surface-active dark
      disabled: colors.gray[600],
      disabledBackground: colors.gray[800],
    },
  },
  components: {
    ...baseTheme.components,
    // Dark mode specific component overrides
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.gray[900],
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.gray[900],
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.gray[900],
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.gray[900],
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          // Ensure alerts use dark-appropriate colors
        },
        standardSuccess: {
          backgroundColor: 'rgba(16, 185, 129, 0.15)',
          color: '#6ee7b7',
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          color: '#fcd34d',
        },
        standardError: {
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          color: '#fca5a5',
        },
        standardInfo: {
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          color: '#93c5fd',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.gray[900],
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: colors.gray[800],
          color: 'rgba(255, 255, 255, 0.95)',
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.2)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.gray[800],
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
          },
        },
        input: {
          color: 'rgba(255, 255, 255, 0.95)',
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.5)',
            opacity: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-focused': {
            color: colors.primary.main,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: 'rgba(255, 255, 255, 0.7)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.gray[800],
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(59, 130, 246, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(59, 130, 246, 0.25)',
            },
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          '&.Mui-selected': {
            backgroundColor: colors.primary.main,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.gray[800],
          color: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
});

/**
 * Returns the appropriate MUI theme based on mode
 * @param {'light' | 'dark'} mode - The theme mode
 * @returns {Theme} MUI theme object
 */
export function getMuiTheme(mode) {
  return mode === 'dark' ? darkTheme : lightTheme;
}
